import type { PopoverSide } from '@/components/primitives/Popover';
import type { RestorationTourStep } from '@/modules/tour/machines/restorationTour';
import type { MapView } from '@/services/appEvents';

/** `card`: floats over the map. `popover`: points at a `[data-tour]` element. */
export type TourStepPlacement = 'card' | 'popover';

export interface TourStepDisplay {
  placement: TourStepPlacement;
  /** `data-tour` value of the popover's anchor. */
  anchor?: string;
  /** Side of the anchor, left-to-right; mirrored for RTL. */
  side?: PopoverSide;
  /** Where the anchor lives; the step hides elsewhere. */
  view: MapView | 'any';
  title: string;
  body: string;
  nextLabel: string;
  /** The opening card, before the tour starts: no step number or progress. */
  welcome?: boolean;
}

/** English source copy, passed through `t()`. */
export const RESTORATION_TOUR_DISPLAY: Record<RestorationTourStep, TourStepDisplay> = {
  intro: {
    placement: 'card',
    view: 'any',
    title: 'Welcome to the Restoration Planner',
    body: 'The Restoration Planner finds areas suited to mangrove restoration. Take a quick tour.',
    nextLabel: 'Begin tour',
    welcome: true,
  },
  glance: {
    placement: 'popover',
    anchor: 'project-panel',
    side: 'right',
    view: 'maps',
    title: 'Your project at a glance',
    body: 'This panel lists the areas in your project. Open one to see its results.',
    nextLabel: 'Next',
  },
  add_area: {
    placement: 'popover',
    anchor: 'add-area',
    side: 'right',
    view: 'maps',
    title: 'Add an area',
    body: 'Use Add new area to outline a stretch of coast you want to screen. You can try it once the tour ends.',
    nextLabel: 'Next',
  },
  read_result: {
    placement: 'popover',
    anchor: 'insight-total-suitability-area-breakdown',
    side: 'left',
    view: 'story',
    title: 'Read the result',
    body: 'The numbers show how much of the demo area was screened and how much is suitable. The bars show how the rest spreads across the score bands.',
    nextLabel: 'Next',
  },
  predictor: {
    placement: 'popover',
    anchor: 'insight-distance-to-water',
    side: 'left',
    view: 'story',
    title: 'An input on the map',
    body: 'Each card is one of the inputs behind the score. Clicking one shows it on the map and adds it to Layers, with its legend.',
    nextLabel: 'Next',
  },
  take_further: {
    placement: 'popover',
    anchor: 'stage-two',
    side: 'left',
    view: 'story',
    title: 'Take it further',
    body: 'Request a Stage 2 assessment to find planting-ready zones in your areas, or download this report as a PDF to share with your team.',
    nextLabel: 'Finish',
  },
};
